import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

/**
 * Dependency-free markdown rendering for the in-app help center (the same
 * philosophy as the SVG charts: a small, closed dialect beats a large
 * dependency). Supports exactly what help topics are written in — headings,
 * paragraphs, bold/italic/inline code, fenced code blocks, lists (one
 * nesting level), pipe tables, blockquotes, rules and links. Raw HTML is
 * never interpreted; everything renders through React, so topic bodies are
 * inert by construction.
 *
 * Links use two schemes: absolute http(s) URLs open in a new tab, and
 * `help:<moduleKey>/<slug>` cross-references resolve against the shell's
 * help base path (`/help` in the panel, `/portal/help` in the portal).
 */

export type Inline =
  | { kind: 'text'; text: string }
  | { kind: 'code'; text: string }
  | { kind: 'strong'; children: Inline[] }
  | { kind: 'em'; children: Inline[] }
  | { kind: 'link'; href: string; children: Inline[] };

export interface ListItem {
  children: Inline[];
  sub?: { ordered: boolean; items: ListItem[] };
}

export type Block =
  | { kind: 'heading'; level: 1 | 2 | 3 | 4; children: Inline[] }
  | { kind: 'paragraph'; children: Inline[] }
  | { kind: 'code'; lang: string; text: string }
  | { kind: 'list'; ordered: boolean; items: ListItem[] }
  | { kind: 'table'; header: Inline[][]; rows: Inline[][][] }
  | { kind: 'quote'; blocks: Block[] }
  | { kind: 'hr' };

// ── Inline parsing ──────────────────────────────────────────────────────────

const LINK_RE = /\[([^\]]+)\]\(([^)\s]+)\)/;
const STRONG_RE = /\*\*([^*]+)\*\*/;
const EM_RE = /\*([^*]+)\*/;

/** Split `text` on the first match of `re`, recursing on the remainder. */
function splitOn(
  text: string,
  re: RegExp,
  make: (match: RegExpMatchArray) => Inline,
  next: (text: string) => Inline[],
): Inline[] | null {
  const match = text.match(re);
  if (!match || match.index === undefined) return null;
  const before = text.slice(0, match.index);
  const after = text.slice(match.index + (match[0]?.length ?? 0));
  return [
    ...(before ? next(before) : []),
    make(match),
    ...(after ? splitOn(after, re, make, next) ?? next(after) : []),
  ];
}

function parseEmphasis(text: string): Inline[] {
  return (
    splitOn(
      text,
      STRONG_RE,
      (m) => ({ kind: 'strong', children: parseEmphasis(m[1] ?? '') }),
      parseEmphasis,
    ) ??
    splitOn(text, EM_RE, (m) => ({ kind: 'em', children: parseEmphasis(m[1] ?? '') }), parseEmphasis) ?? [
      { kind: 'text', text },
    ]
  );
}

function parseLinksAndEmphasis(text: string): Inline[] {
  return (
    splitOn(
      text,
      LINK_RE,
      (m) => ({ kind: 'link', href: m[2] ?? '', children: parseEmphasis(m[1] ?? '') }),
      parseLinksAndEmphasis,
    ) ?? parseEmphasis(text)
  );
}

/** Code spans win over every other inline construct, as in CommonMark. */
export function parseInline(text: string): Inline[] {
  const out: Inline[] = [];
  const parts = text.split(/`([^`]+)`/);
  for (let i = 0; i < parts.length; i++) {
    const part = parts[i];
    if (!part) continue;
    if (i % 2 === 1) out.push({ kind: 'code', text: part });
    else out.push(...parseLinksAndEmphasis(part));
  }
  return out;
}

// ── Block parsing ───────────────────────────────────────────────────────────

const FENCE_RE = /^(```|~~~)\s*(\S*)\s*$/;
const HEADING_RE = /^(#{1,6})\s+(.*)$/;
const HR_RE = /^\s*(-{3,}|\*{3,}|_{3,})\s*$/;
const LIST_ITEM_RE = /^(\s*)([-*]|\d+\.)\s+(.*)$/;
const TABLE_DIVIDER_RE = /^\s*\|?[\s:-]+(\|[\s:-]+)+\|?\s*$/;

function parseTableRow(line: string): Inline[][] {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '');
  return trimmed.split('|').map((cell) => parseInline(cell.trim()));
}

export function parseMarkdown(source: string): Block[] {
  const lines = source.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i] ?? '';

    if (!line.trim()) {
      i++;
      continue;
    }

    const fence = line.match(FENCE_RE);
    if (fence) {
      const close = fence[1] ?? '```';
      const body: string[] = [];
      i++;
      while (i < lines.length && !(lines[i] ?? '').startsWith(close)) {
        body.push(lines[i] ?? '');
        i++;
      }
      i++; // past the closing fence (or end of input)
      blocks.push({ kind: 'code', lang: fence[2] ?? '', text: body.join('\n') });
      continue;
    }

    const heading = line.match(HEADING_RE);
    if (heading) {
      const level = Math.min(heading[1]?.length ?? 1, 4) as 1 | 2 | 3 | 4;
      blocks.push({ kind: 'heading', level, children: parseInline((heading[2] ?? '').trim()) });
      i++;
      continue;
    }

    if (line.startsWith('>')) {
      const quoted: string[] = [];
      while (i < lines.length && (lines[i] ?? '').startsWith('>')) {
        quoted.push((lines[i] ?? '').replace(/^>\s?/, ''));
        i++;
      }
      blocks.push({ kind: 'quote', blocks: parseMarkdown(quoted.join('\n')) });
      continue;
    }

    if (line.includes('|') && TABLE_DIVIDER_RE.test(lines[i + 1] ?? '')) {
      const header = parseTableRow(line);
      i += 2;
      const rows: Inline[][][] = [];
      while (i < lines.length && (lines[i] ?? '').includes('|') && (lines[i] ?? '').trim()) {
        rows.push(parseTableRow(lines[i] ?? ''));
        i++;
      }
      blocks.push({ kind: 'table', header, rows });
      continue;
    }

    if (HR_RE.test(line)) {
      blocks.push({ kind: 'hr' });
      i++;
      continue;
    }

    const item = line.match(LIST_ITEM_RE);
    if (item && item[1]?.length === 0) {
      const ordered = item[2] !== '-' && item[2] !== '*';
      const items: ListItem[] = [];
      while (i < lines.length) {
        const cur = lines[i] ?? '';
        const m = cur.match(LIST_ITEM_RE);
        const parent = items[items.length - 1];
        if (m && m[1]?.length === 0) {
          items.push({ children: parseInline(m[3] ?? '') });
          i++;
        } else if (m && parent) {
          // Indented item — one supported nesting level under the last item.
          const subOrdered = m[2] !== '-' && m[2] !== '*';
          parent.sub ??= { ordered: subOrdered, items: [] };
          parent.sub.items.push({ children: parseInline(m[3] ?? '') });
          i++;
        } else if (/^\s{2,}\S/.test(cur) && parent) {
          // Continuation line: belongs to the previous item's text.
          parent.children.push({ kind: 'text', text: ' ' }, ...parseInline(cur.trim()));
          i++;
        } else {
          break;
        }
      }
      blocks.push({ kind: 'list', ordered, items });
      continue;
    }

    // Paragraph: gather until a blank line or the start of another block.
    const para: string[] = [];
    while (i < lines.length) {
      const cur = lines[i];
      if (
        !cur?.trim() ||
        FENCE_RE.test(cur) ||
        HEADING_RE.test(cur) ||
        cur.startsWith('>') ||
        HR_RE.test(cur) ||
        cur.match(LIST_ITEM_RE)?.[1]?.length === 0
      ) {
        break;
      }
      para.push(cur.trim());
      i++;
    }
    if (para.length > 0) {
      blocks.push({ kind: 'paragraph', children: parseInline(para.join(' ')) });
    }
  }

  return blocks;
}

// ── Rendering ───────────────────────────────────────────────────────────────

const LINK_CLASS = 'font-medium text-brand-600 hover:underline dark:text-brand-400';

function renderInline(nodes: Inline[], basePath: string, keyPrefix: string): ReactNode[] {
  return nodes.map((node, idx) => {
    const key = `${keyPrefix}-${idx}`;
    switch (node.kind) {
      case 'text':
        return node.text;
      case 'code':
        return (
          <code
            key={key}
            className="rounded bg-slate-100 px-1 py-0.5 font-mono text-[0.85em] text-slate-800 dark:bg-slate-800 dark:text-slate-200"
          >
            {node.text}
          </code>
        );
      case 'strong':
        return <strong key={key}>{renderInline(node.children, basePath, key)}</strong>;
      case 'em':
        return <em key={key}>{renderInline(node.children, basePath, key)}</em>;
      case 'link': {
        const label = renderInline(node.children, basePath, key);
        if (node.href.startsWith('help:')) {
          return (
            <Link key={key} to={`${basePath}/${node.href.slice('help:'.length)}`} className={LINK_CLASS}>
              {label}
            </Link>
          );
        }
        if (/^https?:\/\//.test(node.href)) {
          return (
            <a key={key} href={node.href} target="_blank" rel="noreferrer" className={LINK_CLASS}>
              {label}
            </a>
          );
        }
        // Unknown scheme (mailto:, relative, …): render the text, drop the link.
        return <span key={key}>{label}</span>;
      }
    }
  });
}

function renderListItems(
  items: ListItem[],
  basePath: string,
  keyPrefix: string,
): ReactNode[] {
  return items.map((item, idx) => {
    const key = `${keyPrefix}-${idx}`;
    return (
      <li key={key}>
        {renderInline(item.children, basePath, key)}
        {item.sub &&
          (item.sub.ordered ? (
            <ol className="mt-1 list-decimal space-y-1 pl-5">
              {renderListItems(item.sub.items, basePath, `${key}-sub`)}
            </ol>
          ) : (
            <ul className="mt-1 list-disc space-y-1 pl-5">
              {renderListItems(item.sub.items, basePath, `${key}-sub`)}
            </ul>
          ))}
      </li>
    );
  });
}

function renderBlock(block: Block, basePath: string, key: string): ReactNode {
  switch (block.kind) {
    case 'heading': {
      const children = renderInline(block.children, basePath, key);
      switch (block.level) {
        case 1:
          return (
            <h2 key={key} className="mt-8 mb-3 text-xl font-semibold first:mt-0">
              {children}
            </h2>
          );
        case 2:
          return (
            <h2 key={key} className="mt-8 mb-3 text-lg font-semibold first:mt-0">
              {children}
            </h2>
          );
        case 3:
          return (
            <h3 key={key} className="mt-6 mb-2 text-base font-semibold first:mt-0">
              {children}
            </h3>
          );
        default:
          return (
            <h4 key={key} className="mt-5 mb-2 text-sm font-semibold first:mt-0">
              {children}
            </h4>
          );
      }
    }
    case 'paragraph':
      return (
        <p key={key} className="my-3 text-sm leading-6 text-slate-700 dark:text-slate-300">
          {renderInline(block.children, basePath, key)}
        </p>
      );
    case 'code':
      return (
        <pre
          key={key}
          className="my-4 overflow-x-auto rounded-lg bg-slate-900 p-4 font-mono text-xs leading-5 text-slate-100 ring-1 ring-slate-800"
        >
          <code>{block.text}</code>
        </pre>
      );
    case 'list':
      return block.ordered ? (
        <ol
          key={key}
          className="my-3 list-decimal space-y-1 pl-5 text-sm leading-6 text-slate-700 dark:text-slate-300"
        >
          {renderListItems(block.items, basePath, key)}
        </ol>
      ) : (
        <ul
          key={key}
          className="my-3 list-disc space-y-1 pl-5 text-sm leading-6 text-slate-700 dark:text-slate-300"
        >
          {renderListItems(block.items, basePath, key)}
        </ul>
      );
    case 'table':
      return (
        <div key={key} className="my-4 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr>
                {block.header.map((cell, c) => (
                  <th
                    key={c}
                    className="border-b border-slate-300 py-2 pr-4 font-semibold dark:border-slate-700"
                  >
                    {renderInline(cell, basePath, `${key}-h${c}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, r) => (
                <tr key={r}>
                  {row.map((cell, c) => (
                    <td
                      key={c}
                      className="border-b border-slate-100 py-2 pr-4 align-top text-slate-700 dark:border-slate-800 dark:text-slate-300"
                    >
                      {renderInline(cell, basePath, `${key}-${r}-${c}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    case 'quote':
      return (
        <blockquote
          key={key}
          className="my-4 border-l-4 border-brand-300 pl-4 text-slate-600 dark:border-brand-800 dark:text-slate-400"
        >
          {block.blocks.map((b, idx) => renderBlock(b, basePath, `${key}-${idx}`))}
        </blockquote>
      );
    case 'hr':
      return <hr key={key} className="my-6 border-slate-200 dark:border-slate-800" />;
  }
}

/**
 * Renders a help topic body. `basePath` resolves `help:` cross-references
 * (`/help` in the panel shell, `/portal/help` in the portal shell).
 * `hideFirstHeading` drops a leading `#` title when the page chrome already
 * shows it.
 */
export function Markdown({
  source,
  basePath,
  hideFirstHeading = false,
}: {
  source: string;
  basePath: string;
  hideFirstHeading?: boolean;
}) {
  let blocks = parseMarkdown(source);
  if (hideFirstHeading && blocks[0]?.kind === 'heading' && blocks[0].level === 1) {
    blocks = blocks.slice(1);
  }
  return <div>{blocks.map((block, idx) => renderBlock(block, basePath, `b${idx}`))}</div>;
}
